import { AppConfig } from '../app-config.model';

/** Chiave TransferState usata dal server per trasferire la config pubblica al browser. */
export const APP_CONFIG_STATE_KEY = 'APP_CONFIG_PUBLIC';
export const APP_CONFIG_URL = '/config/config.json';
/** Id dello script TransferState generato da Angular (`${APP_ID}-state`, APP_ID di default = 'ng'). */
const NG_STATE_SCRIPT_ID = 'ng-state';

export const EMPTY_APP_CONFIG: AppConfig = {
  SESSIONAPI: '',
  QUOTEAPI: '',
  PRODUCTSAPI: '',
  PRIVATEAREAPI: '',
  LOGINAPI: '',
  CONTENTAPI: '',
  UTILITYAPI: '',
  STORELOCATORAPI: '',
  PORTAL_BASE_URL: '',
  SOD_PORTAL_BASE_URL: '',
  GOOGLE_CLIENT_ID: '',
  GOOGLE_MAPS_KEY: '',
  VAI24BOOKINGURL: '',
  ONETRUSTCOOKIELAWDATADOMAIN: '',
  ENABLECART: true,
  RECAPTCHA_SITE_KEY: '',
  APPLE_CLIENT_ID: '',
  REALM_SSO_PATH: '',
  REALM_NAME: '',
  REALM_CLIENT_ID: '',
  APPLICATIONINSIGHT: '',
  CONTENT_FUL_SET_INFORMATIVI_MIGRATION: '',
  CONTENT_FUL_SET_INFORMATIVI_COLLETTIVI: '',
  ABBANDONA_CARRELLO: '',
  CLICK_DA_DEM: '',
  AVVENUTA_FIRMA_ELETTRONICA: '',
  ACQUISTA_MOP: '',
  CALCOLA_PREVENTIVO: '',
  SALVA_PROPOSTA: '',
  DOWNLOAD_DOCUMENTI: '',
  EXIT_INTENT: '',
  INTERAZIONI_FAQ: '',
  LOGIN_AREA_RISERVATA: '',
  PAGE_VIEW: '',
  CLICK_FIRMA_ELETTRONICA: '',
  INGESTION_KEY: '',
};

export function createEmptyAppConfig(): AppConfig {
  return { ...EMPTY_APP_CONFIG };
}

export function isAppConfigLoaded(cfg: Partial<AppConfig> | null | undefined): cfg is AppConfig {
  return typeof cfg?.LOGINAPI === 'string' && cfg.LOGINAPI.trim().length > 0;
}

export function normalizeApiBase(url: string): string {
  if (!url) {
    return url;
  }
  if (/^https?:\/\//i.test(url)) {
    return `${url.replace(/\/+$/, '')}/`;
  }
  return `/${url.replace(/^\/+|\/+$/g, '')}/`;
}

/** Stessa normalizzazione applicata lato SSR, così browser e server usano URL identici. */
export function normalizeAppConfig(config: Partial<AppConfig>): AppConfig {
  const cfg: AppConfig = { ...EMPTY_APP_CONFIG, ...config };
  cfg.SESSIONAPI = normalizeApiBase(cfg.SESSIONAPI);
  cfg.QUOTEAPI = normalizeApiBase(cfg.QUOTEAPI);
  cfg.PRODUCTSAPI = normalizeApiBase(cfg.PRODUCTSAPI);
  cfg.PRIVATEAREAPI = normalizeApiBase(cfg.PRIVATEAREAPI);
  cfg.LOGINAPI = normalizeApiBase(cfg.LOGINAPI);
  cfg.CONTENTAPI = normalizeApiBase(cfg.CONTENTAPI);
  cfg.UTILITYAPI = normalizeApiBase(cfg.UTILITYAPI);
  cfg.STORELOCATORAPI = normalizeApiBase(cfg.STORELOCATORAPI);
  if (cfg.PORTAL_BASE_URL) {
    cfg.PORTAL_BASE_URL = cfg.PORTAL_BASE_URL.replace(/\/+$/, '');
  }
  return cfg;
}

/**
 * Legge la config dal TransferState serializzato dall'SSR (`<script id="ng-state">`).
 * Sulle rotte `RenderMode.Client` (area-riservata, account) lo script non esiste.
 */
export function readAppConfigFromTransferState(doc: Document): AppConfig | null {
  const script = doc.getElementById(NG_STATE_SCRIPT_ID);
  if (!script?.textContent) {
    return null;
  }
  try {
    const state = JSON.parse(script.textContent) as Record<string, unknown>;
    const cfg = state[APP_CONFIG_STATE_KEY] as Partial<AppConfig> | undefined;
    return isAppConfigLoaded(cfg) ? { ...EMPTY_APP_CONFIG, ...cfg } : null;
  } catch (error) {
    console.warn('[App] Unable to parse TransferState config', error);
    return null;
  }
}

export async function fetchAppConfig(fetchFn: typeof fetch = fetch): Promise<AppConfig | null> {
  try {
    const response = await fetchFn(APP_CONFIG_URL, { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) {
      console.error(`[App] ${APP_CONFIG_URL} returned HTTP ${response.status}`);
      return null;
    }
    const cfg = normalizeAppConfig(await response.json());
    if (!isAppConfigLoaded(cfg)) {
      console.error(`[App] ${APP_CONFIG_URL} loaded but LOGINAPI is missing`);
      return null;
    }
    return cfg;
  } catch (error) {
    console.error(`[App] Unable to load ${APP_CONFIG_URL}`, error);
    return null;
  }
}

/**
 * Risolve la config nel browser PRIMA del bootstrap:
 * 1. TransferState (pagine renderizzate in SSR) -> nessuna richiesta extra;
 * 2. fetch di /config/config.json (pagine CSR o SSR degradato).
 */
export async function resolveBrowserAppConfig(
  doc: Document = document,
  fetchFn: typeof fetch = fetch,
): Promise<AppConfig> {
  return readAppConfigFromTransferState(doc) ?? (await fetchAppConfig(fetchFn)) ?? createEmptyAppConfig();
}
